/**
 * useRegisteredCustomers — paginated read of the customers list at
 * /iam/customers (iam_users where user_type_code='customer').
 *
 * Read-only by design — no create/update/delete. Operators view
 * registered shoppers; customers manage their own profile via the
 * /account/* surface.
 */

import { useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type { RegisteredCustomerDto } from '../models/user.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

// Single-source-of-truth: src/models/user.model.ts.
// Per SOP 02 §"Mutation Patterns (LOCKED)" — DTO source rule.
export type { RegisteredCustomerDto } from '../models/user.model'

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: RegisteredCustomerDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
  }
}

export interface UseRegisteredCustomersReturn {
  readonly items: RegisteredCustomerDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  /** Block (status='suspended') or unblock (status='active') a customer. */
  readonly setStatus: (
    id: string,
    status: 'active' | 'suspended'
  ) => Promise<{ ok: true } | { ok: false; message: string }>
}

export function useRegisteredCustomers(): UseRegisteredCustomersReturn {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<RegisteredCustomerDto, null>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.IAMCUSTOMERS}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          throw new Error((await parseApiError(res)).message)
        }
        const body = (await res.json()) as ListBody
        return {
          items: body.data.items,
          total: body.data.total,
          extras: null
        }
      },
      [authedFetch]
    )
  })

  const setStatus = useCallback(
    async (id: string, status: 'active' | 'suspended') => {
      const r = await mutateJson<RegisteredCustomerDto>(
        authedFetch,
        fillPath(APIROUTES.IAMCUSTOMERSITEM, { id }),
        'PATCH',
        { status }
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      paged.updateItem((it) => it.id === id, r.data)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  return {
    items: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    setStatus
  }
}
